import type {
  InfiniteData,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import {
  type ReactNode,
  type RefObject,
  type UIEventHandler,
  useMemo,
} from "react";
import { SimpleTable } from "@/core/components/simple-table";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { Frame, FrameHeader, FramePanel } from "@volca/ui/components/ui/frame";
import { Stack } from "@volca/ui/components/ui/stack";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { cn } from "@volca/ui/lib/utils";
import type { ApPage } from "@/accounts-payable/types";
import { flattenPages } from "@/accounts-payable/utils";
import { LoadMoreButton } from "./shared";

type ListQuery<TData> = Pick<
  UseInfiniteQueryResult<InfiniteData<ApPage<TData>>>,
  | "data"
  | "isPending"
  | "isError"
  | "isPlaceholderData"
  | "hasNextPage"
  | "fetchNextPage"
  | "isFetching"
  | "isFetchingNextPage"
  | "refetch"
>;

export type ListTableMessage = { title: string; body: string };

export const ListTableEmpty = ({ title, body }: ListTableMessage) => (
  <Empty className={emptyStateVariants({ layout: "collection" })}>
    <EmptyHeader>
      <EmptyTitle>{title}</EmptyTitle>
      <EmptyDescription>{body}</EmptyDescription>
    </EmptyHeader>
  </Empty>
);

/**
 * The AP list tables (Invoices, Receipts, Emails, Vendors): the table card,
 */
export const ListTableCard = <TData extends { id: string }>({
  query,
  label,
  columns,
  selectedId,
  onSelect,
  empty,
  error,
  toolbar,
  className,
  viewportRef,
  onViewportScroll,
  awaitingPosition = false,
  restoring = false,
}: {
  query: ListQuery<TData>;
  label: string;
  columns: ColumnDef<TData>[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  empty: ReactNode;
  error: ListTableMessage;
  toolbar?: ReactNode;
  className?: string;
  viewportRef?: RefObject<HTMLDivElement | null>;
  onViewportScroll?: UIEventHandler<HTMLDivElement>;
  awaitingPosition?: boolean;
  restoring?: boolean;
}) => {
  const {
    data: pages,
    isPending,
    isError,
    isPlaceholderData,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = query;
  // Stable rows prevent the table from rebuilding its row model on each render.
  const data = useMemo(() => flattenPages(pages?.pages), [pages]);
  // A new filter keeps the previous result on screen until its own arrives
  // (placeholderData). Rows from before are fine to show meanwhile; an empty
  // result from before is not: it would announce "nothing today" before the
  // request for today has even answered. Show loading instead.
  const awaitingNewResult = !!isPlaceholderData && data.length === 0;
  const isLoading = isPending || awaitingNewResult || awaitingPosition;
  const showError = isError && (data.length === 0 || restoring);

  const content = showError ? (
    <ErrorState
      title={error.title}
      description={error.body}
      retrying={isFetching}
    >
      <Button
        variant="outline"
        size="sm"
        disabled={isFetching}
        onClick={() => void query.refetch()}
      >
        Try again
      </Button>
    </ErrorState>
  ) : !isLoading && data.length === 0 ? (
    empty
  ) : (
    <>
      {/* Full-bleed rows; the outer columns take a 24px inset, the same one
          the frame header uses for its filters. */}
      <SimpleTable
        label={label}
        data={data}
        columns={columns}
        isLoading={isLoading}
        viewportRef={viewportRef}
        onViewportScroll={onViewportScroll}
        getRowId={(row) => row.id}
        selectedRowId={selectedId}
        onRowClick={(row) => onSelect(row.id)}
        className="min-h-0 w-full flex-1 [&_td:first-child]:pl-6 [&_td:last-child]:pr-6 [&_th:first-child]:pl-6 [&_th:last-child]:pr-6"
        scrollContentStyle={{ minWidth: 900 }}
      />
      {hasNextPage && (
        <Stack align="start" gap={4} className="w-full flex-none py-4">
          <LoadMoreButton
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
            disabled={isFetching}
          />
        </Stack>
      )}
    </>
  );

  if (toolbar)
    return (
      <Frame
        dense
        className={cn(
          "mt-4 flex min-h-0 w-full flex-1 flex-col",
          "[--frame-panel-header-px-base:--spacing(6)] [--frame-panel-px-base:0px] [--frame-panel-py-base:0px]",
          className,
        )}
      >
        <FrameHeader>{toolbar}</FrameHeader>
        <FramePanel className="flex min-h-0 flex-col">{content}</FramePanel>
      </Frame>
    );

  return (
    <Card
      className={cn(
        "mt-4 flex min-h-0 w-full flex-1 flex-col gap-0 overflow-hidden py-0",
        className,
      )}
    >
      {content}
    </Card>
  );
};
